import { PUZZLE_CONFIG } from '../config/puzzleConfig';

// 해결 가능한 퍼즐인지 확인
const isSolvable = (puzzle, size) => {
  let inversions = 0;
  const totalPieces = size * size;
  
  // 역전(inversion) 개수 계산 (빈 칸 제외)
  for (let i = 0; i < totalPieces - 1; i++) {
    for (let j = i + 1; j < totalPieces; j++) {
      if (puzzle[i] !== totalPieces - 1 && puzzle[j] !== totalPieces - 1 && puzzle[i] > puzzle[j]) {
        inversions++;
      }
    }
  }
  
  // 빈 칸의 위치 찾기
  const emptyIndex = puzzle.indexOf(totalPieces - 1);
  const emptyRow = Math.floor(emptyIndex / size);
  
  // console.log(`=== 퍼즐 해결 가능성 분석 (${size}x${size}) ===`);
  // console.log(`퍼즐 배열: [${puzzle.join(', ')}]`);
  // console.log(`역전 개수: ${inversions}`);
  // console.log(`빈 칸 위치: ${emptyIndex} (${emptyRow}번째 행)`);
  
  // 홀수 크기 퍼즐 (3x3, 5x5 등): 역전 개수가 짝수여야 해결 가능
  if (size % 2 === 1) {
    const isSolvable = inversions % 2 === 0;
    //console.log(`홀수 크기 퍼즐: 역전 개수 ${inversions}는 ${inversions % 2 === 0 ? '짝수' : '홀수'} → ${isSolvable ? '풀 수 있음' : '풀 수 없음'}`);
    return isSolvable;
  }
  
  // 짝수 크기 퍼즐 (4x4, 6x6 등): (역전 개수 + 빈 칸의 행 번호)가 홀수여야 해결 가능
  // 빈 칸의 행 번호는 위에서부터 0, 1, 2, 3... (0부터 시작)
  const sum = inversions + emptyRow;
  const isSolvable = sum % 2 === 1;
  //console.log(`짝수 크기 퍼즐: (역전 ${inversions} + 행 ${emptyRow}) = ${sum}은 ${sum % 2 === 0 ? '짝수' : '홀수'} → ${isSolvable ? '풀 수 있음' : '풀 수 없음'}`);
  //console.log(`=== 분석 완료 ===`);
  
  return isSolvable;
};

// 퍼즐 초기화 - 해결 가능한 셔플된 배열 생성
export const initializePuzzle = (size) => {
  const totalPieces = size * size;
  let puzzle = Array.from({ length: totalPieces }, (_, i) => i); // 정답 상태에서 시작
  let shuffleCount = 0;
  const minShuffles = size === 3 ? 25 : 50;
  const maxAttempts = 30; // 💥 퍼즐 생성 최대 시도 횟수 제한
  const minDistance = size * 3; // 퍼즐이 최소 이 정도는 어긋나 있어야 허용
  
  //console.log(`\n=== ${size}x${size} 퍼즐 생성 시작 ===`);
  // 테스트용: 3x3 퍼즐을 1번만 움직이면 완성되도록 설정
  // if (size === 3) {
  //   // 완성된 상태에서 1번만 움직인 상태로 설정
  //   // [0,1,2,3,4,5,6,7,8] → [0,1,2,3,4,8,6,7,5] (마지막 조각과 빈칸만 바뀜)
  //   puzzle = [0, 1, 2, 3, 4, 8, 6, 7, 5];
  //   return puzzle;
  // }
  
  // if (size === 4) {
  //   // 4x4 완성된 상태에서 1번만 움직인 상태로 설정
  //   // [0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15] → [0,1,2,3,4,5,6,7,8,9,10,11,12,13,15,14] (마지막 조각과 빈칸만 바뀜)
  //   puzzle = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 15, 14];
  //   return puzzle;
  // }

  // 맨해튼 거리 합 ≥ N 이상이어야 허용
  let attempt = 0;
  while (attempt < maxAttempts) {
    attempt++;

    puzzle = generateSolvableShuffledPuzzle(size, minShuffles);

    const isTooEasy = getManhattanDistance(puzzle, size) < minDistance;
    const isAlreadySolved = checkComplete(puzzle);

    if (!isTooEasy && !isAlreadySolved) {
      // console.log(`✅ 퍼즐 생성 성공 (시도 ${attempt}회)`);
      // console.log("🧩 최종 퍼즐:", puzzle.join(','));
      // console.log("✅ checkComplete:", checkComplete(puzzle));
      // console.log("✅ isSolvable:", isSolvable(puzzle, size)); // 이 함수도 잠깐 넣어두면 검증 가능

      return puzzle;
    }
  }
};

// 해결 가능한 셔플된 퍼즐 생성
const generateSolvableShuffledPuzzle = (size, minShuffles) => {
  const totalPieces = size * size;
  let puzzle = Array.from({ length: totalPieces }, (_, i) => i);
  let lastMoved = null;
  let shuffleCount = 0;

  while (shuffleCount < minShuffles) {
    const emptyIndex = puzzle.indexOf(totalPieces - 1);
    const emptyRow = Math.floor(emptyIndex / size);
    const emptyCol = emptyIndex % size;

    const movable = [];
    if (emptyRow > 0) movable.push(emptyIndex - size);
    if (emptyRow < size - 1) movable.push(emptyIndex + size);
    if (emptyCol > 0) movable.push(emptyIndex - 1);
    if (emptyCol < size - 1) movable.push(emptyIndex + 1);

    const filtered = movable.filter(i => puzzle[i] !== lastMoved);
    const candidates = filtered.length ? filtered : movable;
    const chosenIndex = candidates[Math.floor(Math.random() * candidates.length)];

    const temp = puzzle[emptyIndex];
    puzzle[emptyIndex] = puzzle[chosenIndex];
    puzzle[chosenIndex] = temp;

    lastMoved = puzzle[emptyIndex];
    shuffleCount++;
  }

  return puzzle;
};

// 맨해튼 거리 합 ≥ N 이상이어야 허용
function getManhattanDistance(puzzle, size) {
  let distance = 0;
  const total = puzzle.length;

  for (let i = 0; i < total; i++) {
    const val = puzzle[i];
    if (val === total - 1) continue; // 빈칸은 무시
    const correctRow = Math.floor(val / size);
    const correctCol = val % size;
    const currRow = Math.floor(i / size);
    const currCol = i % size;

    distance += Math.abs(correctRow - currRow) + Math.abs(correctCol - currCol);
  }

  return distance;
}


// 퍼즐 완성 확인
export const checkComplete = (puzzle) => {
  return puzzle.every((value, index) => value === index);
};

// 인접한 셀인지 확인
export const isAdjacent = (pos1, pos2, size) => {
  const row1 = Math.floor(pos1 / size);
  const col1 = pos1 % size;
  const row2 = Math.floor(pos2 / size);
  const col2 = pos2 % size;

  return (
    (Math.abs(row1 - row2) === 1 && col1 === col2) ||
    (Math.abs(col1 - col2) === 1 && row1 === row2)
  );
};

// 조각 이동 가능 여부 확인
export const canMovePiece = (pieceIndex, puzzle, size) => {
  const emptyIndex = size * size - 1;
  if (pieceIndex === emptyIndex) return false;

  const currentPosition = puzzle.findIndex(value => value === pieceIndex);
  const emptyPosition = puzzle.findIndex(value => value === emptyIndex);

  return isAdjacent(currentPosition, emptyPosition, size);
};

// 조각 이동 실행
export const movePiece = (pieceIndex, puzzle, size) => {
  const emptyIndex = size * size - 1;
  const currentPosition = puzzle.findIndex(value => value === pieceIndex);
  const emptyPosition = puzzle.findIndex(value => value === emptyIndex);

  const newPuzzle = [...puzzle];
  newPuzzle[emptyPosition] = pieceIndex;
  newPuzzle[currentPosition] = emptyIndex;

  return newPuzzle;
};

// 배경 위치 계산
export const getBackgroundPosition = (row, col, size) => {
  return `${(col / (size - 1)) * 100}% ${(row / (size - 1)) * 100}%`;
}; 